import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageCircle, Gauge, Zap, TrendingUp, ArrowRight, BarChart3, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/tuning`;
const REQ_TIMEOUT = 20000;

const toArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Reusable performance calculator card - powers both /calculator
 * and the Izmir landing pages. All API/state logic lives here so
 * consuming pages just render <CalculatorWidget />.
 */
const CalculatorWidget = ({ labels, contact }) => {
  const t = {
    cardTitle: 'Vehicle Performance Calculator',
    subtitle: (n) => `🎯 Real data from ${n} brands • 14,210 engines • 13,164 performance records`,
    step1: '1. Select Brand *',
    step2: '2. Select Model *',
    step3: '3. Select Year/Generation *',
    step4: '4. Select Engine *',
    chooseBrand: 'Choose brand',
    loadingBrands: 'Loading brands...',
    chooseModel: 'Choose model',
    selectBrandFirst: 'Select brand first',
    noModels: 'No models found',
    chooseYear: 'Choose year',
    selectModelFirst: 'Select model first',
    noGenerations: 'No generations found',
    chooseEngine: 'Choose engine',
    selectGenFirst: 'Select generation first',
    noEngines: 'No engines found',
    selectedLine: 'Selected',
    calculate: 'Calculate Performance Gains',
    loading: 'Loading Performance Data...',
    techSpecs: 'Technical Specifications',
    stage1Gains: 'Stage 1 Performance Gains',
    stockPerf: 'Stock Performance',
    stage1Tuned: 'Stage 1 Tuned',
    power: 'Power',
    torque: 'Torque',
    powerGain: 'Power Gain',
    torqueGain: 'Torque Gain',
    powerIncrease: 'Power Increase',
    torqueIncrease: 'Torque Increase',
    gain: 'Gain',
    book: 'Book Your Appointment',
    quote: 'Get Quote on WhatsApp',
    another: '← Calculate Another Vehicle',
    fuelType: 'Type of Fuel',
    method: 'Method',
    tuningType: 'Tuning Type',
    cylinderContent: 'Cylinder Content',
    compressionRatio: 'Compression Ratio',
    boreStroke: 'Bore X Stroke',
    engineNumber: 'Engine Number',
    engineEcu: 'Engine ECU',
    selectEngineFirst: 'Please select an engine first',
    loadErrorTitle: 'Could not load vehicle data',
    loadErrorMsg: 'Please check your connection and try again.',
    retry: 'Retry',
    perfErrorMsg: 'Could not load performance data. Please try again.',
    ...labels,
  };

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [engines, setEngines] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedGeneration, setSelectedGeneration] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('');

  const [performanceData, setPerformanceData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [brandsError, setBrandsError] = useState(false);
  const [inlineError, setInlineError] = useState('');

  const loadBrands = useCallback(async () => {
    try {
      setLoading(true);
      setBrandsError(false);
      setInlineError('');
      const response = await axios.get(`${API}/brands`, { timeout: REQ_TIMEOUT });
      const data = toArray(response.data);
      setBrands(data);
      if (data.length === 0) setBrandsError(true);
    } catch (error) {
      console.error('Error loading brands:', error);
      setBrands([]);
      setBrandsError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const engineId = params.get('engine');
    if (!engineId) return;
    (async () => {
      try {
        setLoadingPerformance(true);
        const response = await axios.get(`${API}/engines/${engineId}/performance`, { timeout: REQ_TIMEOUT });
        if (response.data) {
          setPerformanceData(response.data);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error loading performance:', error);
      } finally {
        setLoadingPerformance(false);
      }
    })();
  }, []);

  const handleBrandChange = async (brandId) => {
    setSelectedBrand(brandId);
    setSelectedModel('');
    setSelectedGeneration('');
    setSelectedEngine('');
    setModels([]);
    setGenerations([]);
    setEngines([]);
    setShowResults(false);
    setInlineError('');
    if (!brandId) return;
    try {
      const response = await axios.get(`${API}/brands/${brandId}/models`, { timeout: REQ_TIMEOUT });
      setModels(toArray(response.data));
    } catch (error) {
      console.error('Error loading models:', error);
      setModels([]);
      setInlineError(t.loadErrorMsg);
    }
  };

  const handleModelChange = async (modelId) => {
    setSelectedModel(modelId);
    setSelectedGeneration('');
    setSelectedEngine('');
    setGenerations([]);
    setEngines([]);
    setShowResults(false);
    setInlineError('');
    if (!modelId) return;
    try {
      const response = await axios.get(`${API}/models/${modelId}/generations`, { timeout: REQ_TIMEOUT });
      setGenerations(toArray(response.data));
    } catch (error) {
      console.error('Error loading generations:', error);
      setGenerations([]);
      setInlineError(t.loadErrorMsg);
    }
  };

  const handleGenerationChange = async (generationId) => {
    setSelectedGeneration(generationId);
    setSelectedEngine('');
    setEngines([]);
    setShowResults(false);
    setInlineError('');
    if (!generationId) return;
    try {
      const response = await axios.get(`${API}/generations/${generationId}/engines`, { timeout: REQ_TIMEOUT });
      setEngines(toArray(response.data));
    } catch (error) {
      console.error('Error loading engines:', error);
      setEngines([]);
      setInlineError(t.loadErrorMsg);
    }
  };

  const loadPerformanceData = async () => {
    if (!selectedEngine) return;
    try {
      setLoadingPerformance(true);
      setInlineError('');
      const response = await axios.get(`${API}/engines/${selectedEngine}/performance`, { timeout: REQ_TIMEOUT });
      if (!response.data || !response.data.performance) {
        setInlineError(t.perfErrorMsg);
        return;
      }
      setPerformanceData(response.data);
      setShowResults(true);
      setTimeout(() => {
        const resultsSection = document.getElementById('results-section');
        if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error loading performance:', error);
      setInlineError(t.perfErrorMsg);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const vehicleLine = performanceData
    ? `${performanceData.brand} ${performanceData.model} ${performanceData.engine}`
    : '';

  const handleWhatsApp = () => {
    const waNumber = contact?.wa || '905327866635';
    let message = performanceData
      ? `Hi, I'm interested in Stage 1 tuning for my ${vehicleLine}`
      : 'Hello, I would like to know more about Powerlab tuning services';
    if (contact?.embed) {
      message = performanceData && t.waText ? t.waText(vehicleLine) : (t.waGeneric || message);
    }
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  };

  const handleMail = () => {
    if (contact?.embed) {
      const to = contact.mail || 'info@powerlab.works';
      const tag = contact.ref ? ` [${contact.ref}]` : '';
      const subject = `${t.mailSubject || 'Stage 1 quote request'}${tag}`;
      const body = performanceData && t.mailBody ? t.mailBody(vehicleLine) : '';
      window.open(`mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_top');
      return;
    }
    window.location.href = 'mailto:info@powerlab.works?subject=Appointment Request&body=Hello, I would like to book an appointment for chip tuning.';
  };

  const perf = performanceData?.performance || {};
  const powerGainPercent = perf.power_original ? ((perf.power_diff / perf.power_original) * 100).toFixed(1) : 0;
  const torqueGainPercent = perf.torque_original ? ((perf.torque_diff / perf.torque_original) * 100).toFixed(1) : 0;

  const selectedBrandName = brands.find(b => b.id.toString() === selectedBrand)?.name || '';
  const selectedModelName = models.find(m => m.id.toString() === selectedModel)?.name || '';
  const selectedGenerationName = generations.find(g => g.id.toString() === selectedGeneration)?.name || '';
  const selectedEngineName = engines.find(e => e.id.toString() === selectedEngine)?.name || '';

  return (
    <Card className="max-w-4xl mx-auto bg-[#1a1a1a] border-gray-800 shadow-2xl" id="calculator">
      <CardHeader className="border-b border-gray-800">
        <CardTitle className="text-2xl text-white flex items-center">
          <BarChart3 className="w-6 h-6 text-[#FF6600] mr-2" />
          {t.cardTitle}
        </CardTitle>
        <p className="text-sm text-gray-400 mt-2">{t.subtitle(brands.length)}</p>
      </CardHeader>
      <CardContent className="p-6 md:p-8">
        {brandsError && (
          <div
            className="mb-6 flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 p-4"
            data-testid="brands-load-error"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-semibold text-sm">{t.loadErrorTitle}</p>
                <p className="text-gray-300 text-sm">{t.loadErrorMsg}</p>
              </div>
            </div>
            <Button
              onClick={loadBrands}
              disabled={loading}
              className="bg-red-500 hover:bg-red-600 text-white flex-shrink-0"
              data-testid="retry-brands-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> {t.retry}</>}
            </Button>
          </div>
        )}

        <div className="space-y-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t.step1}</label>
              <Select value={selectedBrand} onValueChange={handleBrandChange} disabled={loading || brandsError}>
                <SelectTrigger className="bg-black/50 border-gray-700 text-white h-12" data-testid="brand-select-trigger">
                  <SelectValue placeholder={loading ? t.loadingBrands : t.chooseBrand} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t.step2}</label>
              <Select value={selectedModel} onValueChange={handleModelChange} disabled={!selectedBrand || models.length === 0}>
                <SelectTrigger className="bg-black/50 border-gray-700 text-white h-12" data-testid="model-select-trigger">
                  <SelectValue placeholder={selectedBrand ? (models.length === 0 ? t.noModels : t.chooseModel) : t.selectBrandFirst} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {models.map(model => (
                    <SelectItem key={model.id} value={model.id.toString()}>{model.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t.step3}</label>
              <Select value={selectedGeneration} onValueChange={handleGenerationChange} disabled={!selectedModel || generations.length === 0}>
                <SelectTrigger className="bg-black/50 border-gray-700 text-white h-12" data-testid="generation-select-trigger">
                  <SelectValue placeholder={selectedModel ? (generations.length === 0 ? t.noGenerations : t.chooseYear) : t.selectModelFirst} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {generations.map(gen => (
                    <SelectItem key={gen.id} value={gen.id.toString()}>{gen.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t.step4}</label>
              <Select value={selectedEngine} onValueChange={setSelectedEngine} disabled={!selectedGeneration || engines.length === 0}>
                <SelectTrigger className="bg-black/50 border-gray-700 text-white h-12" data-testid="engine-select-trigger">
                  <SelectValue placeholder={selectedGeneration ? (engines.length === 0 ? t.noEngines : t.chooseEngine) : t.selectGenFirst} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {engines.map(engine => (
                    <SelectItem key={engine.id} value={engine.id.toString()}>{engine.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {inlineError && !brandsError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200 flex items-center gap-2" data-testid="inline-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}

          {selectedBrand && selectedModel && selectedGeneration && selectedEngine && (
            <div className="bg-gradient-to-r from-[#FF6600]/10 to-[#E55A00]/10 border border-[#FF6600]/30 rounded-lg p-4">
              <p className="text-white font-semibold">
                {t.selectedLine}: {selectedBrandName} {selectedModelName} • {selectedGenerationName} • {selectedEngineName}
              </p>
            </div>
          )}

          <Button
            onClick={loadPerformanceData}
            className="w-full bg-gradient-to-r from-[#FF6600] to-[#E55A00] hover:from-[#E55A00] hover:to-[#CC4A00] text-white text-lg py-6 mt-4"
            disabled={!selectedEngine || loadingPerformance}
            data-testid="calculate-performance-btn"
          >
            {loadingPerformance ? (
              <><Loader2 className="mr-2 animate-spin" /> {t.loading}</>
            ) : (
              <><BarChart3 className="mr-2" /> {t.calculate}</>
            )}
          </Button>
        </div>

        {showResults && performanceData && perf && (
          <div className="space-y-6 animate-in fade-in duration-500" id="results-section" data-testid="results-section">
            <div className="border-t border-gray-800 pt-6">
              <div className="bg-gradient-to-r from-[#FF6600]/10 to-[#E55A00]/10 border border-[#FF6600]/30 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {performanceData.brand} {performanceData.model}
                </h3>
                <p className="text-gray-300 text-lg">{performanceData.generation}</p>
                <p className="text-[#FF6600] font-semibold text-lg">{performanceData.engine}</p>
              </div>
            </div>

            {performanceData.specs && (
              <div className="border-t border-gray-800 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t.techSpecs}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[
                    ['fuel_type', t.fuelType],
                    ['method', t.method],
                    ['tuning_type', t.tuningType],
                    ['cylinder_content', t.cylinderContent],
                    ['compression_ratio', t.compressionRatio],
                    ['bore_stroke', t.boreStroke],
                    ['engine_number', t.engineNumber],
                    ['engine_ecu', t.engineEcu],
                  ].map(([key, label]) => performanceData.specs?.[key] && (
                    <Card key={key} className="bg-[#0a0a0a] border-gray-800">
                      <CardContent className="p-3">
                        <p className="text-gray-500 text-xs mb-1">{label}</p>
                        <p className="text-white text-sm font-semibold">{performanceData.specs[key]}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-800 pt-6">
              <h3 className="text-lg font-semibold text-white mb-6 text-center">{t.stage1Gains}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card className="bg-black/50 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold">{t.stockPerf}</h4>
                      <Gauge className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-400">{t.power}</span>
                          <span className="text-white font-bold text-xl">{perf.power_original ?? '-'} HP</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-3"><div className="bg-gray-600 h-3 rounded-full" style={{ width: '100%' }} /></div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-400">{t.torque}</span>
                          <span className="text-white font-bold text-xl">{perf.torque_original ?? '-'} NM</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-3"><div className="bg-gray-600 h-3 rounded-full" style={{ width: '100%' }} /></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-[#FF6600]/20 to-[#E55A00]/20 border-[#FF6600]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold">{t.stage1Tuned}</h4>
                      <Zap className="w-5 h-5 text-[#FF6600]" />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-300">{t.power}</span>
                          <span className="text-[#FF6600] font-bold text-xl">{perf.power_tuned ?? '-'} HP</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-3"><div className="bg-gradient-to-r from-[#FF6600] to-[#E55A00] h-3 rounded-full" style={{ width: '100%' }} /></div>
                        <p className="text-green-400 text-sm mt-2 flex items-center">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          +{perf.power_diff ?? 0} HP (+{powerGainPercent}%)
                        </p>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-300">{t.torque}</span>
                          <span className="text-[#FF6600] font-bold text-xl">{perf.torque_tuned ?? '-'} NM</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-3"><div className="bg-gradient-to-r from-[#FF6600] to-[#E55A00] h-3 rounded-full" style={{ width: '100%' }} /></div>
                        <p className="text-green-400 text-sm mt-2 flex items-center">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          +{perf.torque_diff ?? 0} NM (+{torqueGainPercent}%)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-[#0a0a0a] border-gray-800"><CardContent className="p-4 text-center"><p className="text-gray-400 text-sm mb-1">{t.powerGain}</p><p className="text-[#FF6600] text-2xl font-bold">+{perf.power_diff ?? 0}</p><p className="text-gray-500 text-xs">HP</p></CardContent></Card>
                <Card className="bg-[#0a0a0a] border-gray-800"><CardContent className="p-4 text-center"><p className="text-gray-400 text-sm mb-1">{t.torqueGain}</p><p className="text-[#FF6600] text-2xl font-bold">+{perf.torque_diff ?? 0}</p><p className="text-gray-500 text-xs">NM</p></CardContent></Card>
                <Card className="bg-[#0a0a0a] border-gray-800"><CardContent className="p-4 text-center"><p className="text-gray-400 text-sm mb-1">{t.powerIncrease}</p><p className="text-green-400 text-2xl font-bold">{powerGainPercent}%</p><p className="text-gray-500 text-xs">{t.gain}</p></CardContent></Card>
                <Card className="bg-[#0a0a0a] border-gray-800"><CardContent className="p-4 text-center"><p className="text-gray-400 text-sm mb-1">{t.torqueIncrease}</p><p className="text-green-400 text-2xl font-bold">{torqueGainPercent}%</p><p className="text-gray-500 text-xs">{t.gain}</p></CardContent></Card>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <Button
                  onClick={handleMail}
                  className="flex-1 bg-[#FF6600] hover:bg-[#E55A00] text-white py-6 text-lg"
                >
                  {t.book} <ArrowRight className="ml-2" />
                </Button>
                <Button onClick={handleWhatsApp} className="flex-1 bg-[#25D366] hover:bg-[#1DA851] text-white py-6 text-lg">
                  <MessageCircle className="mr-2" /> {t.quote}
                </Button>
              </div>
              <div className="flex justify-center mt-4">
                <Button variant="ghost" onClick={() => { setShowResults(false); setPerformanceData(null); }} className="text-gray-400 hover:text-white">
                  {t.another}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CalculatorWidget;
